import type { MediaProfile, MeetingHostView, MeetingPublic } from '@shared/protocol';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'same-origin',
    headers: init?.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  });

  if (res.status === 204) return undefined as T;

  const body = (await res.json().catch(() => ({}))) as { error?: string };
  if (!res.ok) throw new ApiError(res.status, body.error ?? `Request failed (${res.status})`);
  return body as T;
}

export const api = {
  session: () => request<{ host: string | null }>('/session'),

  logIn: (username: string, password: string) =>
    request<{ host: string }>('/session', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),

  logOut: () => request<void>('/session', { method: 'DELETE' }),

  listMeetings: () => request<{ meetings: MeetingHostView[] }>('/meetings'),

  createMeeting: (title: string, ttlHours?: number, media?: MediaProfile) =>
    request<MeetingHostView>('/meetings', {
      method: 'POST',
      body: JSON.stringify({ title, ttlHours, media }),
    }),

  setMeetingMedia: (code: string, media: MediaProfile) =>
    request<MeetingHostView>(`/meetings/${encodeURIComponent(code)}`, {
      method: 'PATCH',
      body: JSON.stringify({ media }),
    }),

  deleteMeeting: (code: string) => request<void>(`/meetings/${encodeURIComponent(code)}`, { method: 'DELETE' }),

  lookup: (code: string) => request<MeetingPublic>(`/meetings/${encodeURIComponent(code)}/public`),
};
